import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useStableKeyboardOffset } from "@/hooks/useStableKeyboardOffset";
import { useStableFullHeight } from "@/hooks/useStableFullHeight";
import {
  interceptTextFieldMouseDown,
  sheetKeyboardGeometry,
  useSheetFocusRescue,
} from "@/hooks/useKeyboardSafeSheet";
import { registerOpenSheet } from "@/lib/openSheetRegistry";

/**
 * Publishes THIS sheet's own geometry inputs to the debug channel.
 *
 * Why a separate slot rather than reading the existing rows: the strip's
 * `fullHeight` comes from Index.tsx's private `fullHeightRef`, and
 * `__kbDiag.stable` is written by EVERY mounted `useStableKeyboardOffset`
 * — Index's and this sheet's — so the last writer wins and neither row is
 * attributable to the sheet. A sheet rendering two thirds of the screen
 * cannot be diagnosed from a number that might belong to the shell.
 *
 * Self-healing and non-throwing, for the same reason `writeStableDiag` is
 * (useStableKeyboardOffset.ts): a TypeError in a passive effect unmounts the
 * subtree, which remounts an open sheet and replays its slide-in animation —
 * a debug write that breaks the thing being debugged.
 */
const writeSheetGeomDiag = (geom: {
  offset: number;
  fullHeight: number;
  overlayHeight: string;
  top: string;
  keyboardOpen: boolean;
}): void => {
  try {
    const w = window as unknown as Record<string, any>;
    w.__kbDiag = w.__kbDiag || {};
    w.__kbDiag.sheetGeom = geom;
  } catch {
    /* diagnostics must never be able to break the sheet */
  }
};

/**
 * Re-exported so the sheet's own tests keep importing the contract from the
 * component that has to honour it. The rules themselves live in
 * useKeyboardSafeSheet — the Party Planner wizard is the second consumer
 * (Sep 1 2026), and a second copy would have drifted.
 */
export { sheetKeyboardGeometry, isInterceptedTextField } from "@/hooks/useKeyboardSafeSheet";

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /**
   * The default chrome's heading. Optional ONLY because `header` can replace
   * the whole chrome — exactly one of the two must be given, and
   * `bottomSheetChrome` below is what enforces that.
   */
  title?: string;
  subtitle?: string;
  /**
   * #544 — a sheet's OWN chrome, replacing the grabber + title + close row.
   *
   * Added so the hand-rolled 92dvh sheets can adopt this component's keyboard
   * handling WITHOUT adopting its look. Those sheets are near-literal copies of
   * this shell that diverged by accident, but their headers diverged on
   * purpose: PersonalBudget centres its title between a close button and a
   * settings button, and the planner tools use a charcoal bar. Migrating them
   * onto the default chrome would fix a keyboard bug by silently redesigning
   * four screens, which is not a trade anyone asked for.
   *
   * A `header` is rendered flexShrink:0 above the scrollport, in place of the
   * default chrome — it is NOT scrolled, so a sheet keeps its header fixed
   * while its body scrolls, which is the property the keyboard fix depends on.
   */
  header?: ReactNode;
  /**
   * #544 — panel background. Defaults to this component's violet; the migrated
   * sheets pass their own (PersonalBudget is Ghost White #F0F0FF) so the
   * migration is invisible to the user.
   */
  panelBackground?: string;
  /**
   * #544 — the scrollport's TOP and HORIZONTAL padding, for a migrating sheet
   * whose content was laid out against different numbers.
   *
   * ⚠️ The BOTTOM is deliberately NOT overridable and never will be. It is
   * `scrollPaddingBottom` from sheetKeyboardGeometry, and it is not cosmetic:
   * with the keyboard open it IS the last field's entire scroll range. A
   * caller passing a smaller bottom would make the final field unreachable by
   * any amount of scrolling — the exact defect this component exists to
   * prevent, reintroduced through a styling prop.
   */
  contentPaddingTop?: number;
  contentPaddingX?: number;
  /**
   * #544 batch 2 — the rest of a migrating sheet's panel look.
   *
   * `panelClassName` exists so a caller can pass `bg-kin-ghost` rather than a
   * hex: that colour is a CSS token (`--kin-ghost`, src/index.css) and
   * hardcoding #F0F0FF here would silently stop tracking it the first time the
   * token moves. PersonalBudget passes a hex because its original shell did;
   * the planner tools pass the class because theirs did.
   *
   * `panelRadius` / `panelShadow`: the planner sheets use a 16px corner and a
   * lifted shadow where this component defaults to 24px and none. Preserving
   * them is the difference between a keyboard fix and a visible restyle of
   * three screens.
   *
   * ⚠️ Narrow props ON PURPOSE — there is no `panelStyle` escape hatch. A
   * caller able to set arbitrary panel style could set `top`, `bottom` or
   * `height`, which is exactly the geometry the keyboard handling owns; the
   * hand-rolled `height: 92dvh` this whole issue is about would come straight
   * back, as a prop, on a sheet that looks migrated.
   */
  panelClassName?: string;
  panelRadius?: string;
  panelShadow?: string;
  children: ReactNode;
}

/**
 * Which chrome a sheet gets, and the refusal when it asks for neither.
 *
 * Exported for its own test: with `strictNullChecks` off, `title?: string`
 * plus `header?: ReactNode` cannot express "exactly one" in the type system,
 * so the guard has to be real code. A sheet with no chrome at all renders a
 * headerless panel with no close button and no grabber — reachable only by
 * swiping the scrim, which on a full-height sheet reads as the app hanging.
 */
export function bottomSheetChrome(
  title: string | undefined,
  header: ReactNode | undefined,
): "custom" | "default" {
  if (header) return "custom";
  if (title) return "default";
  throw new Error(
    "BottomSheet needs either a `title` or a `header` — a sheet with neither " +
      "has no close affordance.",
  );
}

const BottomSheet = ({
  open,
  onClose,
  title,
  subtitle,
  header,
  panelBackground,
  contentPaddingTop,
  contentPaddingX,
  panelClassName,
  panelRadius,
  panelShadow,
  children,
}: BottomSheetProps) => {
  // Keyboard-offset tracking only mounts for the sheet that's actually open —
  // ~29 BottomSheet instances live in TasksTab at once, and Median's native
  // keyboard bridge is a single-listener API (see useKeyboardOffset.ts "re-arm:
  // Median listen() appears to be one-shot"). Calling the hook unconditionally
  // here would register that many concurrent listeners, the same shape as the
  // June 29 2026 getSession() lock-starvation bug but for the keyboard bridge.
  if (!open) return null;
  return (
    <BottomSheetShell
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      header={header}
      panelBackground={panelBackground}
      contentPaddingTop={contentPaddingTop}
      contentPaddingX={contentPaddingX}
      panelClassName={panelClassName}
      panelRadius={panelRadius}
      panelShadow={panelShadow}
    >
      {children}
    </BottomSheetShell>
  );
};


const BottomSheetShell = ({
  onClose,
  title,
  subtitle,
  header,
  panelBackground,
  contentPaddingTop,
  contentPaddingX,
  panelClassName,
  panelRadius,
  panelShadow,
  children,
}: Omit<BottomSheetProps, "open">) => {
  // Throws on neither — see bottomSheetChrome.
  const chrome = bottomSheetChrome(title, header);
  const keyboardOffset = useStableKeyboardOffset();
  const fullHeight = useStableFullHeight(keyboardOffset);
  const { overlayHeight, top, keyboardTop, keyboardOpen, scrollPaddingBottom } =
    sheetKeyboardGeometry(fullHeight, keyboardOffset);

  // Tells Index.tsx a sheet is up, so it can suppress the un-debounced
  // raw-close release of --vv-height that would otherwise resize the page
  // behind this sheet on every field hop. See openSheetRegistry.
  useEffect(() => registerOpenSheet(), []);

  // Inert diagnostics. Compare against the DOM-measured `sheet:` row in
  // KBDebugStrip: if overlayHeight here matches the measured overlay but both
  // are short, the hooks are wrong; if they disagree, the CSS is.
  useEffect(() => {
    writeSheetGeomDiag({
      offset: keyboardOffset,
      fullHeight,
      overlayHeight,
      top,
      keyboardOpen,
    });
  }, [keyboardOffset, fullHeight, overlayHeight, top, keyboardOpen]);

  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  useSheetFocusRescue({ rootRef, scrollRef, keyboardTop, keyboardOpen });

  // ⚠️ PORTALLED TO document.body — #1755, and this is load-bearing.
  //
  // `position: fixed` is relative to the VIEWPORT only while no ancestor has
  // a transform, will-change, filter or contain. Any of those makes that
  // ancestor the containing block AND a stacking context, so a fixed overlay
  // inside one is sized to that element and its z-index competes only inside
  // it.
  //
  // TasksTab's SwipeableTodayCard sets `transform: translateX(0px)` and
  // `will-change: transform` on every swipeable card (:936, :997). The gift
  // heads-up card renders its sheet inside one, so on Kasandra's phone the
  // sheet was clipped to that one card: the following cards painted straight
  // through it, the backdrop covered nothing, and Save sat below the fold.
  //
  // Portalling is the fix rather than removing the transform, because the
  // transform is what makes the swipe smooth and because ANY future sheet
  // placed inside ANY transformed ancestor would break the same way. Same
  // answer the codebase already uses for PortalDropdown and TimeSlotField.
  //
  // Nothing else moves: the overlay keeps its own classes, geometry and refs,
  // and `useStableKeyboardOffset`/`useSheetFocusRescue` measure elements, not
  // ancestry.
  return createPortal(
    // The overlay, not the panel, absorbs the keyboard. Its bottom edge IS the
    // keyboard top, which is what lets the panel below use a plain `bottom: 0`.
    <div
      ref={rootRef}
      // Inert diagnostics hook: KBDebugStrip's live loop reads this element's
      // rect so the sheet's REAL geometry is visible on a device, not inferred.
      data-kin-sheet-overlay=""
      className="fixed top-0 left-0 right-0 z-[60]"
      style={{ height: overlayHeight }}
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`absolute left-0 right-0 mx-auto flex flex-col overflow-hidden animate-fade-slide-in${panelClassName ? ` ${panelClassName}` : ""}`}
        style={{
          // Constant top + `bottom: 0` inside a --vv-height overlay. Height is
          // implied, so the bottom edge tracks the keyboard while the header
          // never moves. Do not add a `height` here. See sheetKeyboardGeometry.
          top,
          bottom: 0,
          width: "100%",
          maxWidth: 672,
          // ⚠️ An inline backgroundColor BEATS a className, so the default
          // must stand aside when the caller styles the panel by class —
          // otherwise `panelClassName="bg-kin-ghost"` renders violet anyway
          // and the migration silently repaints the sheet.
          backgroundColor: panelBackground || (panelClassName ? undefined : "#E8E6F4"),
          borderRadius: panelRadius || "24px 24px 0 0",
          boxShadow: panelShadow || undefined,
          // NO transition on geometry. Index.tsx's raw writer cycles
          // --vv-height twice per focus change (see sheetKeyboardGeometry), and
          // easing each of those over 250ms is what turned a re-layout into a
          // visible slide. The open/close animation is `animate-fade-slide-in`
          // (transform + opacity), which is unaffected.
        }}
      >
        {chrome === "custom" ? (
          // The sheet's own chrome, OUTSIDE the scrollport and flexShrink:0 —
          // same position the default grabber+title occupies. A header that
          // scrolled would break the property the whole keyboard fix rests on:
          // the panel's top is a constant and only the body moves.
          <div style={{ flexShrink: 0 }} data-kin-sheet-header="">
            {header}
          </div>
        ) : (
        <>
        <div style={{ padding: "10px 0 4px", display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <div
            style={{ width: 40, height: 4, backgroundColor: "#C8C8F4", borderRadius: 2 }}
          />
        </div>
        <div
          // flexShrink:0 with the grabber above: without it these compress
          // under a short column instead of the scrollport, so the title
          // squashes while the form keeps its height.
          style={{ padding: "8px 20px 14px", flexShrink: 0 }}
          className="flex items-start justify-between"
        >
          <div className="flex flex-col min-w-0">
            <span
              className="font-display"
              style={{ fontSize: 22, color: "#1C1A3E", lineHeight: 1.2 }}
            >
              {title}
            </span>
            {subtitle && (
              <span
                style={{
                  fontSize: 12.5,
                  color: "#5050C8",
                  fontStyle: "italic",
                  marginTop: 2,
                }}
              >
                {subtitle}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 flex items-center justify-center"
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              backgroundColor: "#F0F0FF",
              border: "1px solid #E8E6F4",
              fontSize: 13,
              color: "#1C1A3E",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        </>
        )}
        <div
          ref={scrollRef}
          data-kin-sheet-scroll=""
          className="flex-1 overflow-y-auto"
          // SINGLE-SCROLLER RULE (Aug 20). WebKit's own scroll-on-focus and
          // our settle-based rescue were both moving this container — two
          // movers a quarter-second apart is the last residual jitter on
          // field hops. Intercept the synthesized mousedown for text fields
          // and focus with preventScroll, so the rescue is the ONLY thing
          // that ever scrolls this container. The rescue re-runs when the
          // stable offset settles (keyboardTop is in its dep list), so every
          // tapped field still ends up visible — that guarantee is what makes
          // suppressing the native reveal safe.
          //
          // mousedown, not touchstart (preventDefault there would kill drag
          // scrolling) and not touchend (would kill click). Synthesized mouse
          // events only fire for taps, never for scroll drags, so scrolling
          // by dragging on a field is unaffected.
          //
          // Known tradeoff: the FIRST tap into an already-filled field puts
          // the caret at the end instead of the tap point (programmatic focus
          // cannot place a caret). A second tap — the field is now focused,
          // so we stand aside — repositions natively. Already-focused fields
          // are never intercepted for exactly that reason.
          onMouseDown={interceptTextFieldMouseDown}
          style={{
            // minHeight: 0 is required here — a flex column child with
            // overflow-y-auto silently fails to scroll in WebKit and instead
            // overflows its parent (the checklist-overflow bug) unless this
            // is set explicitly; flex's default min-height:auto wins otherwise.
            minHeight: 0,
            // Horizontal containment only — NOT geometry. A child that refuses
            // to shrink (an `input type="date"` is the known one) otherwise
            // scrolls the whole sheet sideways and takes the panel's right edge
            // with it. overflow-y is already `auto` via the className, so this
            // creates no new scroller and the single-scroller rule is intact.
            //
            // Safe for the chip rows in GardenPlannerTool, the Party wizard
            // steps and shared/DayChip: each carries its own `overflow-x: auto`
            // and a descendant scroller is unaffected by an ancestor's hidden
            // overflow.
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            // Bottom stays computed — see contentPaddingTop's header.
            padding: `${contentPaddingTop ?? 20}px ${contentPaddingX ?? 20}px ${scrollPaddingBottom}`,
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BottomSheet;
